import { BaseClient } from './baseClient';
import {
    AuthPrecheckResponse,
    AuthLoginResponse,
    EnquirySubmission,
    EnquiryResponse,
    EmailVerificationConfirmation
} from '../types/auth.types';

export class AuthClient extends BaseClient {
    constructor() {
        super();
    }

    async checkUserStatus(email: string): Promise<AuthPrecheckResponse> {
        const response = await this.api.post<AuthPrecheckResponse>('/auth/email/precheck', { email });
        console.log({ response });

        return response.data;
    }

    async login(firebaseUser: {
        displayName: string | null;
    }): Promise<AuthLoginResponse> {
        const response = await this.api.post<AuthLoginResponse>('/auth/login', {
            fullName: firebaseUser.displayName
        });
        return response.data;
    }

    async getMe(): Promise<AuthLoginResponse> {
        const response = await this.api.get<AuthLoginResponse>('/auth/me');
        return response.data;
    }

    /**
     * Records that the user dismissed the dashboard welcome. No body — the
     * server takes the identity from the token and the time from the request.
     * Returns the updated user, so the caller refreshes its cached copy from
     * the response instead of re-fetching /auth/me.
     */
    async dismissDashboardWelcome(): Promise<AuthLoginResponse> {
        const response = await this.api.post<AuthLoginResponse>('/auth/me/onboarding/dashboard-welcome/dismiss');
        return response.data;
    }

    /**
     * Asks the backend to email a password-reset link. PUBLIC, and the answer
     * is the same whether or not the address has an account, so any 2xx is
     * "sent" and the caller learns nothing about the address. The link opens
     * `/auth/action?mode=resetPassword&oobCode=...` in this app.
     */
    async requestPasswordReset(email: string): Promise<void> {
        await this.api.post('/auth/password-resets', { email });
    }

    /**
     * Asks the backend to email the sign-up verification link. The token is
     * passed in rather than read by the interceptor because the caller signs
     * the Firebase session out straight after. Any 2xx = accepted. The link
     * opens `/auth/action?mode=verifyEmail&token=...` in this app.
     */
    async requestEmailVerification(idToken: string): Promise<void> {
        await this.api.post('/auth/email-verifications', {}, {
            headers: { Authorization: `Bearer ${idToken}` },
        });
    }

    /**
     * Confirms the address behind a verification link's `token`. The backend
     * owns verification end to end (Firebase's flag, the user record) and
     * answers with the address plus, when it can, a Firebase custom token to
     * sign the person in with. PUBLIC — the person has no session yet.
     * 200 → `{ email, customToken? }`; a spent or expired link is 410
     * (`LINK_EXPIRED`); any failure rejects with the axios error.
     */
    async confirmEmailVerification(token: string): Promise<EmailVerificationConfirmation> {
        const response = await this.api.post<EmailVerificationConfirmation>('/auth/email-verifications/confirm', { token });
        return response.data;
    }

    /**
     * "Talk to our team" enquiry from the logged-out login page.
     * PUBLIC endpoint — no auth required; the base-client interceptor only
     * attaches a token when a Firebase user exists, so nothing is forced
     * here. Rate limited server-side (429 on the 6th submit/hour/IP).
     */
    async submitEnquiry(payload: EnquirySubmission): Promise<EnquiryResponse> {
        const response = await this.api.post<EnquiryResponse>('/auth/enquiries', payload);
        return response.data;
    }
}

const authClient = new AuthClient();
export default authClient;
